import { type UIElement, UIElementStyle } from '@smart-tv/typings/content';
import { UIElementContentSource } from '@smart-tv/hooks/use-ui-element-content/use-ui-element-content';
import { FAVORITES_STRIPE_ID } from '@smart-tv/components/stripes/_utils/types';
import { Area, UIElementStripe, IUiElement } from '@norlys-a-s/norlys-play-kmm';
import { logNetworkEvent } from '../network-log';
import { checkNetwork } from './check-network';

import {
  flowSubscribe,
  getFavoriteListIdentifierForKmm,
  handleKmmError,
  type FlowSubscription
} from './kmm';

const KMM_STYLE_VALUES = new Set<string>(Object.values(UIElementStyle));

const mapStripeStyle = (stripe: UIElementStripe): UIElementStyle => {
  const styleName = (stripe as { style?: { name?: string } }).style?.name;
  return styleName && KMM_STYLE_VALUES.has(styleName)
    ? (styleName as UIElementStyle)
    : UIElementStyle.Mixed;
};

const stripeResponseMapper = (uiElements: ReadonlyArray<IUiElement>): UIElement[] => {
  const stripes = uiElements.filter(
    (uiElement): uiElement is UIElementStripe => 'storageKey' in uiElement
  );
  return stripes.map((stripe) => ({
    id: stripe.id,
    title: stripe.title,
    style: mapStripeStyle(stripe),
    contentSource: UIElementContentSource.UIelements,
    disablePagination: true,
    ...(stripe.id === FAVORITES_STRIPE_ID && { properties: { update_on_favorite: true } })
  }));
};

//KMM upgrade 17.0.0 -> 18.0.14 resulted in KMM expecting the field 'actionValue' instead of 'parameterValue' in the ActionParameterNetworkModel object.
// To solve this we use the call that uses the api version 3 for the 'getUiElement' call and filter the result to only include UIElementStripes.
// When sections get introduced on the front page in the future we will need to update this function to handle sections as well.
// - ASO
export function subscribeUiElementsFlow(
  favoritesListId = -2,
  area: Area = Area.Frontpage,
  onData: (uiElements: UIElement[]) => void
): FlowSubscription {
  let cancelled = false;

  const subscription = flowSubscribe((repo) =>
    repo
      .jsGetUiElementsAndUiSectionsForArea(area, getFavoriteListIdentifierForKmm(favoritesListId))
      .subscribe(
        (result) => {
          if (cancelled) return;

          const uiElements = result.getOrThrow().items.asJsReadonlyArrayView();
          const mapped = stripeResponseMapper(uiElements);
          logNetworkEvent('jsGetUiElementsAndUiSectionsForArea', 'FLOW', 'success', {
            params: { favoritesListId, area },
            response: mapped
          });
          onData(mapped);
        },
        async (error) => {
          if (cancelled) return;

          logNetworkEvent('jsGetUiElementsAndUiSectionsForArea', 'FLOW', 'error', {
            params: { favoritesListId, area },
            error
          });
          if (!(await checkNetwork()) || cancelled) return;
          handleKmmError(error);
        }
      )
  );

  return {
    cancel: () => {
      cancelled = true;
      subscription.cancel();
    }
  };
}
